/**
 * Typed API client — all calls flow through this so the Mini App never
 * touches `fetch` directly. Responses are validated with zod.
 */
import { z } from 'zod';
import {
  apiErrorSchema,
  bookingDtoSchema,
  citySchema,
  leaderboardResponseSchema,
  subscriptionDtoSchema,
  subscriptionListResponseSchema,
  createShareResponseSchema,
  sharedTripSchema,
  meSchema,
  publicUserSchema,
  rideDtoSchema,
  rideStatusUpdateDtoSchema,
  rideTemplateDtoSchema,
  routeSchema,
} from '@waylo/shared';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

let sessionToken: string | null = null;

export function setSessionToken(t: string | null): void {
  sessionToken = t;
  if (t) {
    try {
      localStorage.setItem('waylo_session', t);
    } catch {
      /* ignore */
    }
  } else {
    try {
      localStorage.removeItem('waylo_session');
    } catch {
      /* ignore */
    }
  }
}

export function loadSessionToken(): string | null {
  if (sessionToken) return sessionToken;
  try {
    const stored = localStorage.getItem('waylo_session');
    if (stored) sessionToken = stored;
  } catch {
    /* ignore */
  }
  return sessionToken;
}

async function request<T>(path: string, schema: z.ZodType<T>, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = loadSessionToken();
  if (token) headers['X-Waylo-Session'] = token;
  const res = await fetch(url.toString(), {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) {
    const parsed = apiErrorSchema.safeParse(json);
    if (parsed.success) {
      throw new ApiError(res.status, parsed.data.error.code, parsed.data.error.message, parsed.data.error.details);
    }
    throw new ApiError(res.status, 'UNKNOWN', `Request failed (${res.status})`);
  }
  return schema.parse(json);
}

function safeJsonParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

const authResponseSchema = z.object({
  me: meSchema,
  sessionToken: z.string(),
  isNew: z.boolean(),
});

export async function authWithTelegram(initData: string, referralCode?: string) {
  const r = await request('/api/v1/auth/telegram', authResponseSchema, {
    method: 'POST',
    body: { initData, referralCode },
  });
  setSessionToken(r.sessionToken);
  return r;
}

export async function authDev(telegramId?: number, firstName?: string) {
  const r = await request('/api/v1/auth/dev', authResponseSchema, {
    method: 'POST',
    body: { telegramId, firstName },
  });
  setSessionToken(r.sessionToken);
  return r;
}

export async function logout() {
  await request('/api/v1/auth/logout', z.object({ ok: z.boolean() }), { method: 'POST' });
  setSessionToken(null);
}

export async function getMe() {
  return request('/api/v1/me', meSchema);
}

export async function updateMe(patch: Record<string, unknown>) {
  return request('/api/v1/me', meSchema, { method: 'PATCH', body: patch });
}

export async function listRoutes() {
  return request('/api/v1/routes', z.object({ items: z.array(routeSchema) }));
}

/** Quick-pick cities (shown before typing). `country` = 'RS'|'HR'|'BA'|'all'. */
export async function quickPickCities(country?: string) {
  const qs = country && country !== 'all' ? `?country=${encodeURIComponent(country)}` : '';
  return request(`/api/v1/cities${qs}`, z.object({ items: z.array(citySchema) }));
}

/** Prefix search across the (language-scoped) place catalogue. */
export async function searchCitiesApi(q: string, country?: string) {
  const params = new URLSearchParams({ q });
  if (country && country !== 'all') params.set('country', country);
  return request(`/api/v1/cities/search?${params.toString()}`, z.object({ items: z.array(citySchema) }));
}

export async function searchRides(query: Record<string, string | number | boolean | undefined>) {
  return request(
    '/api/v1/rides',
    z.object({ items: z.array(rideDtoSchema), nextCursor: z.string().nullable() }),
    { query },
  );
}

export async function getRide(id: string) {
  return request(`/api/v1/rides/${id}`, rideDtoSchema);
}

/** Edit a published/full ride. Only fields present in `patch` are changed;
 * the server rejects locked fields (RIDE_EDIT_LOCKED) once the ride is booked. */
export async function editRideApi(id: string, patch: Record<string, unknown>) {
  return request(`/api/v1/rides/${id}`, rideDtoSchema, { method: 'PATCH', body: patch });
}

export async function listMyRides() {
  return request('/api/v1/rides/mine', z.object({ items: z.array(rideDtoSchema) }));
}

export async function listTemplates() {
  return request('/api/v1/templates', z.object({ items: z.array(rideTemplateDtoSchema) }));
}

export async function createTemplateApi(body: Record<string, unknown>) {
  return request('/api/v1/templates', z.object({ id: z.string() }), {
    method: 'POST',
    body,
  });
}

export async function cancelTemplateApi(id: string, cancelFutureRides = true) {
  return request(
    `/api/v1/templates/${id}/cancel`,
    z.object({ cancelledRides: z.number() }),
    { method: 'POST', body: { cancelFutureRides } },
  );
}

export async function publishRideApi(body: Record<string, unknown>) {
  return request(
    '/api/v1/rides',
    z.object({
      id: z.string(),
      status: z.string(),
      flagged: z.boolean(),
      flagReason: z.string().nullable(),
    }),
    { method: 'POST', body },
  );
}

export async function listRideStatus(rideId: string) {
  return request(
    `/api/v1/rides/${rideId}/status`,
    z.object({ items: z.array(rideStatusUpdateDtoSchema) }),
  );
}

export async function postRideStatusApi(
  rideId: string,
  status: 'leaving_soon' | 'at_pickup' | 'departed' | 'arrived',
  note?: string,
) {
  return request(`/api/v1/rides/${rideId}/status`, z.object({ id: z.string() }), {
    method: 'POST',
    body: { status, note },
  });
}

export async function cancelRide(id: string, reason?: string) {
  return request(
    `/api/v1/rides/${id}/cancel`,
    z.object({ id: z.string(), status: z.string() }),
    { method: 'POST', body: { reason } },
  );
}

export async function getLeaderboard() {
  return request('/api/v1/leaderboard', leaderboardResponseSchema);
}

export async function listSubscriptions() {
  return request('/api/v1/subscriptions', subscriptionListResponseSchema);
}

export async function createSubscription(body: {
  originCityId: string;
  destinationCityId: string;
  maxPrice?: number;
  womenOnly?: boolean;
}) {
  return request('/api/v1/subscriptions', subscriptionDtoSchema, { method: 'POST', body });
}

export async function deleteSubscription(id: string) {
  return request(`/api/v1/subscriptions/${id}`, z.object({ ok: z.boolean() }), {
    method: 'DELETE',
  });
}

export async function createShareLink(bookingId: string) {
  return request('/api/v1/share', createShareResponseSchema, {
    method: 'POST',
    body: { bookingId },
  });
}

/** Public — works without a session (for family opening the share link). */
export async function getSharedTrip(token: string) {
  return request(`/api/v1/share/${token}`, sharedTripSchema);
}

export async function revokeShareLink(token: string) {
  return request(`/api/v1/share/${token}/revoke`, z.object({ ok: z.boolean() }), {
    method: 'POST',
  });
}

export async function startRideApi(id: string) {
  return request(`/api/v1/rides/${id}/start`, z.object({ id: z.string(), status: z.string() }), {
    method: 'POST',
  });
}

export async function finishRideApi(id: string) {
  return request(`/api/v1/rides/${id}/finish`, z.object({ id: z.string(), status: z.string() }), {
    method: 'POST',
  });
}

export async function confirmRideApi(id: string) {
  return request(`/api/v1/rides/${id}/confirm`, z.object({ ok: z.boolean() }), { method: 'POST' });
}

export async function confirmBookingApi(id: string) {
  return request(`/api/v1/bookings/${id}/confirm`, z.object({ ok: z.boolean() }), {
    method: 'POST',
  });
}

export async function markNoShowApi(id: string) {
  return request(`/api/v1/bookings/${id}/no-show`, z.object({ id: z.string(), status: z.string() }), {
    method: 'POST',
  });
}

export async function bookRide(
  rideId: string,
  seats: number,
  messageToDriver?: string,
  luggage?: 'small' | 'medium' | 'large',
  companions?: string[],
  segment?: { fromCityId: string; toCityId: string },
) {
  return request(
    `/api/v1/rides/${rideId}/book`,
    z.object({ id: z.string(), status: z.string() }),
    {
      method: 'POST',
      body: {
        seats,
        messageToDriver,
        luggage,
        companions,
        fromCityId: segment?.fromCityId,
        toCityId: segment?.toCityId,
      },
    },
  );
}

export async function getBooking(id: string) {
  // BookingDtoSchema already includes rideId (defined in @waylo/shared).
  return request(`/api/v1/bookings/${id}`, bookingDtoSchema);
}

export async function listMyBookings() {
  return request('/api/v1/bookings/mine', z.object({ items: z.array(bookingDtoSchema) }));
}

export async function listIncomingBookings() {
  return request('/api/v1/bookings/incoming', z.object({ items: z.array(bookingDtoSchema) }));
}

export async function bookingTransition(
  id: string,
  action: 'approve' | 'decline' | 'cancel' | 'complete',
) {
  return request(
    `/api/v1/bookings/${id}/${action}`,
    z.object({ id: z.string(), status: z.string() }),
    { method: 'POST' },
  );
}

export async function rateBookingApi(
  id: string,
  body: { stars: number; comment?: string; tags?: string[] },
) {
  return request(`/api/v1/bookings/${id}/rate`, z.object({ ok: z.boolean() }), {
    method: 'POST',
    body,
  });
}

export async function createReport(body: {
  reportedUserId?: string;
  rideId?: string;
  bookingId?: string;
  reason: string;
  description?: string;
}) {
  return request('/api/v1/reports', z.object({ id: z.string() }), { method: 'POST', body });
}

export async function getPublicUser(id: string) {
  return request(`/api/v1/users/${id}`, publicUserSchema);
}

export async function blockUserApi(id: string, reason?: string) {
  return request(`/api/v1/users/${id}/block`, z.object({ ok: z.boolean() }), {
    method: 'POST',
    body: { reason },
  });
}

export async function unblockUserApi(id: string) {
  return request(`/api/v1/users/${id}/unblock`, z.object({ ok: z.boolean() }), {
    method: 'POST',
  });
}

export async function triggerSafetyAlertApi(
  bookingId: string,
  body: { note?: string; notifyEmergencyContact: boolean },
) {
  return request(
    `/api/v1/bookings/${bookingId}/safety-alert`,
    z.object({ alertId: z.string(), emergencyContactNotified: z.boolean() }),
    { method: 'POST', body },
  );
}

export async function createPremiumInvoiceLink() {
  return request('/api/v1/premium/purchase', z.object({ url: z.string() }), {
    method: 'POST',
    body: { plan: 'monthly' },
  });
}
